import { html, property, state } from 'lit-element';
import { nothing } from 'lit-html';
import { KatLitElement, Keys, register } from '../../shared/base';
import baseStyles from '../../shared/base/base.lit.scss';
import { _Emitter, truncateString } from '../../shared/utils';
import { KatToastGroup, Toast } from './toast-group';
import styles from './toast.lit.scss';

export interface FeatureResult {
  dismissalType: 'closed' | 'expired' | 'bumped';
}

const TOAST_DEFAULT_LIFESPAN = 5000;
const TOAST_CHARACTER_LIMIT = 110;

@register('kat-toast')
export class KatToast extends KatLitElement {
  constructor(toastData: Toast) {
    super();
    if (toastData) {
      const {
        description,
        id,
        persistent,
        variant,
        actionText,
        ctaClicked,
        toastClosed,
        toastOpened,
      } = toastData;
      this._toastData = toastData;
      this.id = id;
      this.variant = variant ?? this.variant;
      this.description = description;
      this.persistent = persistent || false;
      this.actionText = actionText;
      this.ctaClicked = ctaClicked;
      this.toastClosed = toastClosed;
      this.toastOpened = toastOpened;
    }
  }

  static get styles() {
    return [baseStyles, styles];
  }

  /** The text that will be displayed.*/
  @property()
  description: string;

  @property()
  removed?: boolean;

  /** The text to display in the CTA link*/
  @property({ attribute: 'action-text' })
  actionText?: string;

  /** Used by the ToastGroup to manage toasts. Must be created manually by the user */
  @property()
  id: string;

  /**
   * If true, toast will not auto-dismiss. If false, user needs to click X to dismiss it.
   */
  @property()
  persistent = false;

  /** The reference to the kat-toast-group that is coordinating this toast. */
  @property()
  _toastGroup?: KatToastGroup;

  /**
   * The state of the toast - Default is info
   * @enum {value} info Info toast - Default
   * @enum {value} success Success toast
   * @enum {value} danger Danger toast
   * @enum {value} warning Warning toast
   */
  @property()
  variant: 'info' | 'warning' | 'danger' | 'success' = 'info';

  // The following callbacks: Provides better experience for React users than a standard event listener.
  /** The optional callback that fires when a cta is clicked. */
  @property({ type: Function, attribute: false })
  ctaClicked?: (toast: Toast) => void;

  /** The optional callback that fires when a toast is closed. */
  @property({ type: Function, attribute: false })
  toastClosed?: (toast: Toast) => void;

  /** The optional callback that fires when a toast is opened. */
  @property({ type: Function, attribute: false })
  toastOpened?: (toast: Toast) => void;

  @state()
  _toastData?: Toast;

  @state()
  _formattedDescription?: string;

  @state()
  lifespan: number = TOAST_DEFAULT_LIFESPAN;

  /**
   * Internal event emitter
   */
  private _dismissEmitter = new _Emitter<{
    toast: KatToast;
  }>();

  _dismissToast() {
    this._dismissEmitter.emit({ toast: this });
  }

  private _emitToastClosedEvent() {
    if (this.toastClosed) {
      this.toastClosed(this._toastData);
    }
    if (this._toastGroup) {
      this._toastGroup._hide(this);
      this._toastGroup._toastClosedEventEmitter.emit({
        toast: this._toastData,
      });
    }
  }

  private _actionOnKey(e: { keyCode: number }) {
    if (Keys.Confirm.includes(e.keyCode)) {
      this._actionClicked();
    }
  }

  /**
   * This method is called by the toastGroup and should not be called by users.
   * When a toast is 'opened', it means it is being displayed at the top of the toast queue
   * and will either be dismissed via button press or expired due to a timer.
   *
   * It waits for the first of two promises:
   * - First promise is the timer (only if toast is not persistent)
   * - Second promise listens for the 'X' button to be pressed by user (via _dismiss emitter)
   */
  _open(): Promise<FeatureResult> {
    if (this.toastOpened) {
      this.toastOpened(this._toastData);
    }
    if (this._toastGroup) {
      this._toastGroup._toastOpenedEventEmitter.emit({
        toast: this._toastData,
      });
    }

    const allPromises = [];
    let expiryTimer;
    let dismissButtonListener: () => void;

    let expiryTimerPromise: Promise<FeatureResult['dismissalType']>;
    if (!this.persistent) {
      expiryTimerPromise = new Promise(resolve => {
        expiryTimer = setTimeout(() => {
          resolve('expired');
        }, this.lifespan);
      });
      allPromises.push(expiryTimerPromise);
    }

    const buttonClickPromise: Promise<FeatureResult['dismissalType']> =
      new Promise(resolve => {
        dismissButtonListener = () => {
          clearTimeout(expiryTimer);
          resolve('closed');
        };
        this._dismissEmitter.addListener(dismissButtonListener);
      });

    allPromises.push(buttonClickPromise);

    return Promise.race(allPromises)
      .then(dismissalType => {
        this._emitToastClosedEvent();
        return { dismissalType, toast: this };
      })
      .finally(() => {
        if (expiryTimer) {
          clearTimeout(expiryTimer);
        }
        this._dismissEmitter.removeListener(dismissButtonListener);
      });
  }

  private _actionClicked() {
    if (this.ctaClicked) {
      this.ctaClicked(this._toastData);
    }

    if (this._toastGroup) {
      this._toastGroup._ctaClickedEventEmitter.emit({ toast: this._toastData });
    }
  }

  render() {
    return html`
      <kat-alert
        role="alert"
        variant=${this.variant}
        description=${this._formattedDescription}
        @dismiss=${this._dismissToast}
        >${this.actionText &&
        html`<kat-link
          tabindex="0"
          slot="cta"
          role="action"
          part="toast-cta-button"
          @keydown=${this._actionOnKey}
          @click=${this._actionClicked}
          >${this.actionText}</kat-link
        >`}</kat-alert
      >
      ${!this.persistent
        ? html`<div class="expiring-timer-bar"></div>`
        : nothing}
    `;
  }

  updated(changedProps: Map<string, any>) {
    if (changedProps.has('description')) {
      this._formattedDescription = truncateString(
        this.description,
        TOAST_CHARACTER_LIMIT,
        true
      );
    }
    if (changedProps.has('lifespan')) {
      this.style.setProperty('--toast-lifespan', Number(this.lifespan) + 'ms');
    }
  }
}
